// Delete grace period in the console (ADR-0031 section 10, registry 0.15.0): the delete dialog, the toast with
// Undo, the "Pending deletion (n)" filter with Restore, and "Delete now" behind its own typed confirmation.
// The pages give the texts; this file has the behaviour, so the three pages act the same.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api/client";
import type {
  AffectedGrant,
  AffectedMember,
  DomainDeleted,
  GraceCollection,
  ImpactCollection,
  Member,
  Pending,
  Principal,
  Role,
  TenantDetail,
} from "../api/types";
import { formatTime } from "../lib/format";
import {
  actorLabel,
  DELETED_TEXT,
  deleteButtonLabel,
  IMPACT_UNKNOWN_TEXT,
  impactLines,
  purgeBlockedText,
  restoreUntilText,
} from "../lib/grace";
import { atLeast } from "../lib/roles";
import { useServices } from "./context";
import { ErrorBanner, useAction } from "./ui";
import { useLoad } from "./useLoad";

export interface DeleteTarget {
  id: string;
  /** Short name for the dialog heading and the toast, for example `trust config "gitlab"`. */
  title: string;
  /** What stops when it is deleted. */
  message: string;
  /** When set, the admin must type this value to delete (ADR-0031 section 5). */
  typedName?: string;
  /** Label of the typed value, for example "trust config name". */
  typedLabel?: string;
  /** Sent as `?confirm=` on the DELETE (domains, api.md 2.1). */
  confirmParam?: string;
  /** The dialog loads what the delete would cut off (api.md 2.8 impact) for this collection, with `id`. */
  impact?: ImpactCollection;
  /**
   * The value to type when the impact says "in use" or cannot be loaded, for example the principal name. Not needed
   * when `typedName` is set: then the admin always types.
   */
  inUseName?: string;
  inUseLabel?: string;
}

export interface PurgeTarget {
  id: string;
  title: string;
  /** What the hard delete also removes, for example "Its grants are deleted too." */
  cascade?: string;
  /** The value the API needs in `confirm`: the name, or the grant ID. */
  confirm: string;
  confirmLabel: string;
}

/** One row of the pending list, made by the page from a pending object. */
export interface PendingRow {
  id: string;
  title: ReactNode;
  detail?: ReactNode;
  /** A warning on the row, for example for API keys. */
  warning?: string;
  deletedBy: string | null;
  deletedAt: string;
  purgeAfter: string;
  purge: PurgeTarget;
  /** False hides Restore and "Delete now", for example an owner member for an admin (ADR-0024 point 1). */
  canManage?: boolean;
}

type Notice =
  | { kind: "deleted"; id: string; title: string; purgeAfter: string | null; details?: string[] }
  | { kind: "info"; text: string };

/** Lines for what a domain delete affected (the DELETE result, api.md 2.1). Empty when the result has no lists. */
export function affectedLines(res: {
  affected_members?: AffectedMember[];
  affected_grants?: AffectedGrant[];
}): string[] {
  const lines: string[] = [];
  const members = res.affected_members ?? [];
  const grants = res.affected_grants ?? [];
  if (members.length) {
    lines.push(
      `Members no longer on a verified domain: ${members.map((m) => m.email ?? m.user_id).join(", ")}.`,
    );
  }
  if (grants.length) {
    const g = grants.map((x) => `${x.namespace}/${x.package_glob} to ${x.grantee.tenant_slug}`);
    lines.push(`Grants paused: ${g.join(", ")}.`);
  }
  return lines;
}

/**
 * The API calls of one grace collection. The default uses the generic routes of api.md 2.8; OCI repositories,
 * tags and manifests (api.md 4.1) give their own.
 */
export interface GraceOps<T> {
  /** A stable key for the pending list load, for example "oci-tags:rep_01". */
  key: string;
  listPending: () => Promise<{ items: Pending<T>[] }>;
  remove: (
    target: DeleteTarget,
  ) => Promise<{ purge_after?: string | null } & Partial<Omit<DomainDeleted, "ok">>>;
  /** Returns an extra line for the toast, or nothing. */
  restore: (id: string) => Promise<string | void>;
  purge: (id: string, confirm: string) => Promise<unknown>;
}

/** State and actions for the delete grace period of one list page. */
export function useGrace<T extends { id: string }>(opts: {
  tenant: TenantDetail;
  /** A collection with the generic routes, or `ops` for other objects. */
  collection?: GraceCollection;
  ops?: GraceOps<T>;
  /** The role that may restore and purge. Default admin; namespaces need owner. */
  role?: Role;
  /** Reloads the live list after a delete or a restore. */
  onChange: () => void;
}) {
  const { api } = useServices();
  const { tenant, collection, onChange } = opts;
  const ops: GraceOps<T> = opts.ops ?? {
    key: collection!,
    listPending: () => api.listPending<T>(tenant.slug, collection!),
    remove: (target) => api.deleteObject(tenant.slug, collection!, target.id, target.confirmParam),
    restore: async (id) => void (await api.restoreObject(tenant.slug, collection!, id)),
    purge: (id, confirm) => api.purgeObject(tenant.slug, collection!, id, confirm),
  };
  const isAdmin = atLeast(tenant.role, opts.role ?? "admin");
  const canListMembers = atLeast(tenant.role, "admin");
  const [view, setView] = useState<"live" | "pending">("live");
  // The ops object is new on each render; the key names the list, so the load runs once per list.
  const pending = useLoad(() => ops.listPending(), [tenant.slug, ops.key]);
  // Names for "Deleted by". Only admins can list members; others see the user ID.
  const members = useLoad<{ items: Member[] } | null>(
    () => (canListMembers ? api.listMembers(tenant.slug) : Promise.resolve(null)),
    [tenant.slug, canListMembers],
  );
  const principals = useLoad<{ items: Principal[] } | null>(
    () => api.listPrincipals(tenant.slug),
    [tenant.slug, ops.key],
  );
  const [toDelete, setToDelete] = useState<DeleteTarget | null>(null);
  const [toPurge, setToPurge] = useState<PurgeTarget | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const action = useAction();
  const dialogAction = useAction();

  const users = new Map((members.data?.items ?? []).map((m) => [m.user_id, m.email ?? m.user_id]));
  const prnNames = new Map((principals.data?.items ?? []).map((p) => [p.id, p.name]));
  const items = pending.data?.items ?? [];

  function changed() {
    onChange();
    pending.reload();
  }

  async function confirmDelete() {
    if (!toDelete) return;
    const target = toDelete;
    const ok = await dialogAction.run(async () => {
      const res = await ops.remove(target);
      setNotice({
        kind: "deleted",
        id: target.id,
        title: target.title,
        purgeAfter: res.purge_after ?? null,
        details: affectedLines(res),
      });
    });
    if (ok) {
      setToDelete(null);
      changed();
    }
  }

  async function restore(id: string, title: string) {
    let extra = "";
    const ok = await action.run(async () => {
      extra = (await ops.restore(id)) ?? "";
    });
    if (ok) {
      setNotice({ kind: "info", text: extra ? `Restored ${title}. ${extra}` : `Restored ${title}.` });
      changed();
    }
  }

  async function confirmPurge() {
    if (!toPurge) return;
    const target = toPurge;
    let blocked: string | null = null;
    const ok = await dialogAction.run(async () => {
      try {
        await ops.purge(target.id, target.confirm);
      } catch (e) {
        const text = collection ? purgeBlockedText(collection) : null;
        if (e instanceof ApiError && e.status === 409 && text) {
          blocked = text;
          return;
        }
        throw e;
      }
    });
    if (!ok) return;
    setToPurge(null);
    setNotice({ kind: "info", text: blocked ?? `Deleted ${target.title} now. You cannot restore it.` });
    pending.reload();
  }

  function rowsTable(rows: PendingRow[]) {
    return (
      <PendingTable
        rows={rows}
        isAdmin={isAdmin}
        busy={action.busy}
        actor={(id) => actorLabel(id, users, prnNames)}
        onRestore={(row, title) => void restore(row.id, title)}
        onPurge={(row) => {
          dialogAction.setError(null);
          setToPurge(row.purge);
        }}
      />
    );
  }

  const ui = (
    <>
      {toDelete && (
        <DeleteDialog
          key={toDelete.id}
          tenant={tenant.slug}
          target={toDelete}
          busy={dialogAction.busy}
          error={dialogAction.error}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setToDelete(null)}
        />
      )}
      {toPurge && (
        <PurgeDialog
          key={toPurge.id}
          target={toPurge}
          busy={dialogAction.busy}
          error={dialogAction.error}
          onConfirm={() => void confirmPurge()}
          onCancel={() => setToPurge(null)}
        />
      )}
      {notice && (
        <Toast
          notice={notice}
          busy={action.busy}
          onUndo={(id, title) => void restore(id, title)}
          onClose={() => setNotice(null)}
        />
      )}
    </>
  );

  return {
    isAdmin,
    view,
    setView,
    items: items as Pending<T>[],
    count: pending.data ? items.length : null,
    error: action.error ?? (view === "pending" ? pending.error : null),
    busy: action.busy || dialogAction.busy,
    askDelete: (t: DeleteTarget) => {
      dialogAction.setError(null);
      setToDelete(t);
    },
    rowsTable,
    /** Loads the pending list again, for a change made by another list (a manifest delete pends its tags). */
    reloadPending: pending.reload,
    /** Restores an object by ID, with the toast. For "Restore it instead" after a create gives 409. */
    restore: (id: string, title: string) => restore(id, title),
    ui,
  };
}

/** "Active" and "Pending deletion (n)" filter above a list. */
export function PendingFilter({
  view,
  setView,
  count,
}: {
  view: "live" | "pending";
  setView: (v: "live" | "pending") => void;
  count: number | null;
}) {
  return (
    <div className="filter" role="group" aria-label="Show">
      <button
        type="button"
        className="secondary"
        aria-pressed={view === "live"}
        onClick={() => setView("live")}
      >
        Active
      </button>
      <button
        type="button"
        className="secondary"
        aria-pressed={view === "pending"}
        onClick={() => setView("pending")}
      >
        Pending deletion{count === null ? "" : ` (${count})`}
      </button>
    </div>
  );
}

/**
 * "Restore it instead" for a create that gives 409 because the name is pending deletion (api.md 2.8). Shows nothing
 * for other errors.
 */
export function RestoreInstead({
  error,
  busy,
  onRestore,
}: {
  error: unknown;
  busy: boolean;
  onRestore: (pending: { id: string; purge_after: string }) => void;
}) {
  if (!(error instanceof ApiError) || error.status !== 409 || !error.pendingDeletion) return null;
  const pending = error.pendingDeletion;
  return (
    <p className="row">
      <button type="button" className="secondary" disabled={busy} onClick={() => onRestore(pending)}>
        Restore it instead
      </button>
    </p>
  );
}

function PendingTable({
  rows,
  isAdmin,
  busy,
  actor,
  onRestore,
  onPurge,
}: {
  rows: PendingRow[];
  isAdmin: boolean;
  busy: boolean;
  actor: (id: string | null) => string;
  onRestore: (row: PendingRow, title: string) => void;
  onPurge: (row: PendingRow) => void;
}) {
  if (rows.length === 0) return <p className="muted">Nothing is pending deletion.</p>;
  return (
    <table className="pending data-table">
      <thead>
        <tr>
          <th>Item</th>
          <th>Deleted by</th>
          <th>Deleted</th>
          <th>Purged after</th>
          <th className="actions">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>
              <span className="pending-title">{r.title}</span>
              {r.detail && <span className="small muted pending-detail">{r.detail}</span>}
              {r.warning && <span className="small pending-warning">{r.warning}</span>}
            </td>
            <td data-label="Deleted by">{actor(r.deletedBy)}</td>
            <td data-label="Deleted">{formatTime(r.deletedAt)}</td>
            <td data-label="Purged after">{formatTime(r.purgeAfter)}</td>
            <td className="actions" data-label="Actions">
              {isAdmin && r.canManage !== false && (
                <>
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => onRestore(r, r.purge.title)}
                  >
                    Restore
                  </button>
                  <button type="button" className="danger" disabled={busy} onClick={() => onPurge(r)}>
                    Delete now
                  </button>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Modal dialog frame: Escape cancels; focus goes to the first field, else to Cancel. */
function Dialog({ title, onCancel, children }: { title: string; onCancel: () => void; children: ReactNode }) {
  const headingId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>("input, button[data-cancel]");
    first?.focus();
  }, []);
  return (
    <div className="dialog-backdrop">
      <div
        ref={ref}
        className="dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
      >
        <h2 id={headingId}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function TypedConfirm({
  label,
  expected,
  value,
  onChange,
}: {
  label: string;
  expected: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label>
      <span>
        Type the {label} <code>{expected}</code> to confirm
      </span>
      <input value={value} autoComplete="off" spellCheck={false} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

/** Loads the impact of a delete target. `null` data: no impact for this target. */
function useImpact(tenant: string, target: DeleteTarget) {
  const { api } = useServices();
  return useLoad(
    () => (target.impact ? api.getImpact(tenant, target.impact, target.id) : Promise.resolve(null)),
    [tenant, target.impact, target.id],
  );
}

/** The typed value the dialog needs now: always, when in use, when the impact fails, or none. */
export function typedFor(
  target: DeleteTarget,
  impact: { in_use: boolean } | null | undefined,
  impactFailed: boolean,
): { name: string; label: string } | null {
  if (target.typedName !== undefined) return { name: target.typedName, label: target.typedLabel ?? "name" };
  if (target.impact && target.inUseName !== undefined && (impactFailed || impact?.in_use)) {
    return { name: target.inUseName, label: target.inUseLabel ?? "name" };
  }
  return null;
}

export function DeleteDialog({
  tenant,
  target,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  tenant: string;
  target: DeleteTarget;
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  // The deadline shown on the button. The response gives the exact purge time for the toast.
  const [openedAt] = useState(() => Date.now());
  const impact = useImpact(tenant, target);
  const loading = Boolean(target.impact) && impact.loading;
  const failed = Boolean(target.impact) && !impact.loading && Boolean(impact.error);
  const need = typedFor(target, impact.data, failed);
  const ready = !loading && (need === null || typed === need.name);
  return (
    <Dialog title={`Delete ${target.title}?`} onCancel={onCancel}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !busy) onConfirm();
        }}
      >
        <p>{target.message}</p>
        <p className="muted">It stops working at once. You can restore it for 3 days.</p>
        {target.impact && (
          <div className="impact" aria-live="polite" data-testid="delete-impact">
            {loading && <p className="muted small">Checking what this delete affects…</p>}
            {failed && <p className="banner warn">{IMPACT_UNKNOWN_TEXT}</p>}
            {impact.data && (
              <ul className={impact.data.in_use ? "impact-lines in-use" : "impact-lines"}>
                {impactLines(impact.data).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {need && <TypedConfirm label={need.label} expected={need.name} value={typed} onChange={setTyped} />}
        <ErrorBanner error={error} />
        <div className="row dialog-buttons">
          <button type="submit" className="danger" disabled={!ready || busy}>
            {deleteButtonLabel(openedAt)}
          </button>
          <button type="button" className="secondary" data-cancel onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function PurgeDialog({
  target,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  target: PurgeTarget;
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const ready = typed === target.confirm;
  return (
    <Dialog title={`Delete ${target.title} now?`} onCancel={onCancel}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !busy) onConfirm();
        }}
      >
        <p className="banner error">This cannot be undone. Prego deletes it at once, with no restore.</p>
        {target.cascade && <p>{target.cascade}</p>}
        <TypedConfirm
          label={target.confirmLabel}
          expected={target.confirm}
          value={typed}
          onChange={setTyped}
        />
        <ErrorBanner error={error} />
        <div className="row dialog-buttons">
          <button type="submit" className="danger" disabled={!ready || busy}>
            Delete now
          </button>
          <button type="button" className="secondary" data-cancel onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function Toast({
  notice,
  busy,
  onUndo,
  onClose,
}: {
  notice: Notice;
  busy: boolean;
  onUndo: (id: string, title: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="toast" role="status">
      {notice.kind === "deleted" ? (
        <p>
          {DELETED_TEXT}
          {restoreUntilText(notice.purgeAfter) && (
            <span className="small toast-until">{restoreUntilText(notice.purgeAfter)}</span>
          )}
          {notice.details?.map((line) => (
            <span key={line} className="small toast-until toast-detail">
              {line}
            </span>
          ))}
        </p>
      ) : (
        <p>{notice.text}</p>
      )}
      <div className="row">
        {notice.kind === "deleted" && (
          <button type="button" disabled={busy} onClick={() => onUndo(notice.id, notice.title)}>
            Undo
          </button>
        )}
        <button type="button" className="link" aria-label="Close the message" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
